import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Alert,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from '@mui/material';
import { Edit, Delete } from '@mui/icons-material';
import { CategoryFilters } from './CategoryFilters';
import { CategoryTable } from './CategoryTable';
import { CategoryDialog } from './CategoryDialog';
import { LoadingSkeleton } from './LoadingSkeleton';
import { useCategories } from '@/hooks';
import type { Category, CreateCategoryRequest } from '@/types/assessment';
import toast from 'react-hot-toast';

export const CategoryManagement: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedCategory, setSelectedCategory] = useState<Category | null>(null);
  const [categoryToDelete, setCategoryToDelete] = useState<Category | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [currentPage] = useState(1);

  const {
    categories,
    loading,
    error,
    fetchCategories,
    createCategory,
    updateCategory,
    deleteCategory,
  } = useCategories();

  // Fetch categories on mount and when filters change
  useEffect(() => {
    const params: any = {
      page: currentPage,
      limit: 20,
      sort: '-createdAt',
    };

    if (searchTerm) params.search = searchTerm;
    if (statusFilter) params.isActive = statusFilter;

    fetchCategories(params);
  }, [searchTerm, statusFilter, currentPage, fetchCategories]);

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, category: Category) => {
    setAnchorEl(event.currentTarget);
    setSelectedCategory(category);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedCategory(null);
  };

  const handleCreateNew = () => {
    setSelectedCategory(null);
    setOpenDialog(true);
  };

  const handleEdit = () => {
    setOpenDialog(true);
    // Don't call handleMenuClose here - keep selectedCategory for the dialog
  };

  const handleDelete = () => {
    setCategoryToDelete(selectedCategory);
    setOpenDeleteDialog(true);
    handleMenuClose();
  };

  const handleDeleteConfirm = async () => {
    console.log('Delete confirm clicked', categoryToDelete);
    if (!categoryToDelete) {
      console.log('No category to delete');
      return;
    }

    try {
      console.log('Calling deleteCategory with ID:', categoryToDelete._id);
      await deleteCategory(categoryToDelete._id);
      console.log('Delete successful');
      toast.success('Category deleted successfully');
      
      // Refresh the list
      const params: any = {
        page: currentPage,
        limit: 20,
        sort: '-createdAt',
      };
      if (searchTerm) params.search = searchTerm;
      if (statusFilter) params.isActive = statusFilter;
      
      fetchCategories(params);
    } catch (error) {
      console.error('Delete failed:', error);
      toast.error('Failed to delete category');
    }
    
    setCategoryToDelete(null);
    setOpenDeleteDialog(false);
  };

  const handleDeleteCancel = () => {
    setCategoryToDelete(null);
    setOpenDeleteDialog(false);
  };

  const handleSubmit = async (data: CreateCategoryRequest) => {
    try {
      if (selectedCategory) {
        await updateCategory(selectedCategory._id, data);
        toast.success('Category updated successfully');
      } else {
        await createCategory(data);
        toast.success('Category created successfully');
      }

      // Refresh the list
      const params: any = {
        page: currentPage,
        limit: 20,
        sort: '-createdAt',
      };
      if (searchTerm) params.search = searchTerm;
      if (statusFilter) params.isActive = statusFilter;
      
      fetchCategories(params);
    } catch (error) {
      toast.error(selectedCategory ? 'Failed to update category' : 'Failed to create category');
      throw error; // Re-throw to prevent dialog from closing
    }
  };

  const handleRefresh = () => {
    const params: any = {
      page: currentPage,
      limit: 20,
      sort: '-createdAt',
    };
    if (searchTerm) params.search = searchTerm;
    if (statusFilter) params.isActive = statusFilter;
    
    fetchCategories(params);
  };

  return (
    <Box>
      <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
        Category Management
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <CategoryFilters
        searchTerm={searchTerm}
        statusFilter={statusFilter}
        onSearchChange={setSearchTerm}
        onStatusChange={setStatusFilter}
        onRefresh={handleRefresh}
        onCreateNew={handleCreateNew}
      />

      {loading ? (
        <LoadingSkeleton rows={5} />
      ) : (
        <CategoryTable
          categories={categories}
          onMenuClick={handleMenuClick}
        />
      )}

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleEdit}>
          <Edit sx={{ mr: 1 }} />
          Edit
        </MenuItem>
        <MenuItem onClick={handleDelete}>
          <Delete sx={{ mr: 1 }} />
          Delete
        </MenuItem>
      </Menu>

      {/* Create/Edit Dialog */}
      <CategoryDialog
        open={openDialog}
        category={selectedCategory}
        onClose={() => {
          setOpenDialog(false);
          setSelectedCategory(null);
          handleMenuClose();
        }}
        onSubmit={handleSubmit}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={openDeleteDialog}
        onClose={handleDeleteCancel}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">
          Confirm Delete
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Are you sure you want to delete this category? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>
            Cancel
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};